
import React from 'react';
import { Trash2, X } from 'lucide-react';
import { useLanguage } from '../i18nContext';

interface DeleteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  description?: string;
  itemType: string; // e.g., "Recording", "Folder", "Scheme"
}

export default function DeleteModal({ isOpen, onClose, onConfirm, title, description, itemType }: DeleteModalProps) {
  const { t } = useLanguage();

  if (!isOpen) return null;

  // Translate itemType
  const getLocalizedItemType = () => {
    if (itemType === 'Folder') return t.dashboard.folders.replace('文件夹', '').trim() || 'Folder';
    if (itemType === 'File') return t.dashboard.newRecording.replace('新建', '').replace('New ', '').trim() || 'File';
    return itemType;
  };

  const localizedItemType = getLocalizedItemType();

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 font-sans text-brand-900">
      {/* Backdrop - Organic Blur */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity animate-in fade-in duration-500"
        onClick={onClose}
      ></div>

      {/* Modal Card */}
      <div className="relative w-full max-w-md bg-surface-light rounded-modal shadow-modal-heavy p-8 animate-in zoom-in-95 fade-in slide-in-from-bottom-4 duration-500 border border-surface-border flex flex-col items-center text-center overflow-hidden">
        {/* Texture Overlay */}
        <div className="absolute inset-0 bg-noise opacity-[0.03] pointer-events-none mix-blend-overlay" />

        {/* Close Button (Top Right) */}
        <button
          onClick={onClose}
          className="absolute top-6 right-6 p-2 text-ink-muted hover:text-brand-900 hover:bg-surface-hover rounded-full transition-colors z-10"
        >
          <X size={20} />
        </button>

        {/* 1. Professional Icon - Subtle & Prestige */}
        <div className="mb-6 relative">
          <div className="w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center border border-red-100/50 shadow-sm">
            <Trash2 size={28} className="text-feedback-error" strokeWidth={1.5} />
          </div>
          {/* Subtle glow behind icon */}
          <div className="absolute -inset-2 bg-feedback-error/5 blur-xl rounded-full -z-10" />
        </div>

        {/* 2. Content */}
        <h2 className="text-2xl font-serif font-bold text-brand-900 mb-3 tracking-tight relative z-10">
          {t.deleteModal.deleteTitle} {localizedItemType}?
        </h2>

        <p className="text-ink-secondary text-sm font-light leading-relaxed mb-8 max-w-[85%] relative z-10">
          {description || `${t.deleteModal.deleteDescription} "${title}"。${t.deleteModal.cannotUndo}`}
        </p>

        {/* 3. Actions */}
        <div className="flex items-center gap-4 w-full relative z-10">
          {/* Cancel Button - Secondary Recipe */}
          <button
            onClick={onClose}
            className="flex-1 py-3 px-6 rounded-2xl font-medium text-sm bg-surface-DEFAULT text-brand-900 border border-surface-border hover:bg-surface-hover hover:-translate-y-0.5 transition-all duration-200"
          >
            {t.deleteModal.cancel}
          </button>

          {/* Delete Button - Premium Error style */}
          <button
            onClick={onConfirm}
            className="flex-1 py-3 px-6 rounded-2xl font-bold text-sm bg-feedback-error text-white shadow-md hover:bg-red-600 hover:shadow-lg hover:-translate-y-0.5 active:scale-[0.98] transition-all duration-200 flex items-center justify-center gap-2"
          >
            <Trash2 size={16} />
            {t.deleteModal.confirm}
          </button>
        </div>
      </div>
    </div>
  );
}
